import { animate, query, state, style, transition, trigger } from '@angular/animations';
import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatListItem, MatNavList } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { ApplicationService } from 'app-shell-data';
import { DrawerComponent } from 'common-ui';
import { ProductFastSearchResults, ProductQuickSearchComponentStore } from 'product-quick-search-data';
import { delay, map } from 'rxjs';
import { ProductQuickSearchItemComponent } from '../product-quick-search-item';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'ideal-product-quick-search-results',
  standalone: true,
  imports: [
    MatButtonModule,
    MatNavList,
    MatListItem,
    RouterLink,
    ProductQuickSearchItemComponent,
    MatProgressSpinnerModule,
    NgTemplateOutlet,
    JsonPipe,
  ],
  templateUrl: './product-quick-search-results.component.html',
  styleUrl: './product-quick-search-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('growResults', [
      state('void', style({ height: '0px', overflow: 'hidden' })),
      state('true', style({ height: '*' })),
      transition(':enter', [
        query('mat-nav-list', [style({ position: 'absolute', bottom: '0px', left: '0px', right: '0px' })]),
        animate('30s ease-in-out'),
      ]),
    ]),
  ],
})
export class ProductQuickSearchResultsComponent {
  private readonly _store = inject(ProductQuickSearchComponentStore);
  private readonly _app = inject(ApplicationService);

  public readonly searchResults = input.required<ProductFastSearchResults>();
  public readonly searchResultsMore = input<{
    byPartNumber: boolean;
    byDescription: boolean;
    byKeyword: boolean;
    byCrossReference: boolean;
  }>();
  public readonly searchTerm = input.required<string>();

  protected readonly byPartNumber = computed(() => this.searchResults().byPartNumber);
  protected readonly byDescription = computed(() => this.searchResults().byDescription);
  protected readonly byKeyword = computed(() => this.searchResults().byKeyword);
  protected readonly byCrossReference = computed(() => this.searchResults().byCrossReference);

  protected readonly loading = computed(() => this._store.loading());
  protected readonly byPartNumberLoading = computed(() => this.loading().byPartNumber);
  protected readonly byDescriptionLoading = computed(() => this.loading().byDescription);
  protected readonly byKeywordLoading = computed(() => this.loading().byKeyword);
  protected readonly byCrossReferenceLoading = computed(() => this.loading().byCrossReference);

  protected readonly byPartNumberMore = computed(() => this.searchResultsMore()?.byPartNumber);
  protected readonly byDescriptionMore = computed(() => this.searchResultsMore()?.byDescription);
  protected readonly byKeywordMore = computed(() => this.searchResultsMore()?.byKeyword);
  protected readonly byCrossReferenceMore = computed(() => this.searchResultsMore()?.byCrossReference);

  protected readonly byPartNumberMoreLink = computed(() => {
    return {
      q: `partNumber:start('${this.searchTerm()}')`,
    };
  });

  protected readonly byDescriptionMoreLink = computed(() => {
    return {
      q: `description:contains('${this.searchTerm()}')`,
    };
  });

  protected readonly byKeywordMoreLink = computed(() => {
    return {
      q: `keyword:start('${this.searchTerm()}')`,
    };
  });

  protected readonly byCrossReferenceMoreLink = computed(() => {
    return {
      q: `crossReference:start('${this.searchTerm()}')`,
    };
  });

  protected readonly hasCrossReferences = computed(() => this._store.hasCrossReferences());

  protected readonly showResultsByPartNumber = computed(() => this.byPartNumber().length > 0);
  protected readonly showResultsByDescription = computed(() => this.byDescription().length > 0);
  protected readonly showResultsByKeyword = computed(() => this.byKeyword().length > 0);
  protected readonly showResultsByCrossReference = computed(() => this.byCrossReference().length > 0);

  protected onResultClick() {
    this._app.closePopovers();
  }
}
